/**
 * CasesPage — очередь тикетов жалоб (docs/architecture/order-complaints.md).
 * Вкладки по статусам, фильтр по машине, сводка «что повторяется».
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listCases, getCaseStats, type CaseRow, type CaseStats, type CaseStatus } from '../api/client';
import { useMachines } from '../hooks/useMachines';

export const CATEGORY_LABEL: Record<string, string> = {
  nothing_printed: 'Nothing printed',
  bad_print: 'Bad print',
  money_not_returned: 'Money not returned',
  charged_twice: 'Charged twice',
  other: 'Other',
};

export const STATUS_LABEL: Record<CaseStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  waiting_customer: 'Waiting for customer',
  waiting_bank_account: 'Waiting for bank account',
  to_pay: 'To pay',
  resolved: 'Resolved',
  rejected: 'Rejected',
};

export const STATUS_COLOR: Record<CaseStatus, { bg: string; fg: string }> = {
  new: { bg: 'rgba(239,68,68,0.15)', fg: '#b91c1c' },
  in_progress: { bg: 'rgba(59,130,246,0.15)', fg: '#1d4ed8' },
  waiting_customer: { bg: 'rgba(234,179,8,0.18)', fg: '#a16207' },
  waiting_bank_account: { bg: 'rgba(234,179,8,0.18)', fg: '#a16207' },
  to_pay: { bg: 'rgba(168,85,247,0.15)', fg: '#7e22ce' },
  resolved: { bg: 'rgba(22,163,74,0.15)', fg: '#15803d' },
  rejected: { bg: 'rgba(100,116,139,0.15)', fg: '#475569' },
};

export const ROOT_CAUSE_LABEL: Record<string, string> = {
  printer_slow_output: 'Printer slow (sheets came out later)',
  printer_jam: 'Printer jam',
  paper_out: 'Out of paper',
  bad_file: 'Bad file (password, corrupt)',
  coin_payout_short: 'Coin change short',
  bill_not_returned: 'Banknote not returned',
  card_double_charge: 'Card charged twice',
  kiosk_frozen: 'Kiosk frozen',
  customer_error: 'Customer error',
  unknown: 'Unknown',
};

export function StatusBadge({ status }: { status: CaseStatus }) {
  const c = STATUS_COLOR[status] ?? STATUS_COLOR.rejected;
  return (
    <span style={{ fontSize: '0.74rem', fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

const TABS: Array<{ key: string; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'new', label: 'New' },
  { key: 'to_pay', label: 'To pay' },
  { key: 'waiting_customer', label: 'Waiting' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

function ago(s: string): string {
  const m = Math.round((Date.now() - new Date(s).getTime()) / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export function CasesPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('status') ?? 'open';
  const machineParam = Number(params.get('machine'));
  const machineId = Number.isInteger(machineParam) && machineParam > 0 ? machineParam : undefined;
  const { machines } = useMachines();
  const [rows, setRows] = useState<CaseRow[] | null>(null);
  const [stats, setStats] = useState<CaseStats | null>(null);
  const [statsDays, setStatsDays] = useState(30);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    listCases({ status: tab || undefined, machineId })
      .then((r) => { setRows(r); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [tab, machineId]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { getCaseStats(statsDays).then(setStats).catch(() => setStats(null)); }, [statsDays]);

  function setTab(k: string) {
    const next = new URLSearchParams(params);
    if (k) next.set('status', k); else next.delete('status');
    setParams(next, { replace: true });
  }
  function setMachine(id: number | undefined) {
    const next = new URLSearchParams(params);
    if (id) next.set('machine', String(id)); else next.delete('machine');
    setParams(next, { replace: true });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>Complaints</h2>
        <div className="flex gap-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-input" style={{ width: 'auto', padding: '4px 8px' }} value={machineId ?? ''} onChange={(e) => setMachine(Number(e.target.value) || undefined)}>
            <option value="">All machines</option>
            {machines.map((m) => <option key={m.machineId} value={m.machineId}>{m.name} (#{m.machineId})</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
        </div>
      </div>

      {error && <div className="card mb-6"><div className="card-body" style={{ color: 'var(--color-offline)' }}>Error: {error}</div></div>}

      {stats && (
        <div className="card mb-6">
          <div className="card-header flex items-center justify-between">
            <span>What repeats · {stats.total} complaints, {stats.open} open, {stats.refundedCzk} Kč refunded
              {stats.medianHoursToResolve != null && <> · median {stats.medianHoursToResolve} h to resolve</>}
            </span>
            <div className="flex gap-2">
              {[7, 30, 90, 365].map((d) => (
                <button key={d} className={`btn btn-sm ${d === statsDays ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setStatsDays(d)}>{d}d</button>
              ))}
            </div>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <StatList title="By category" rows={stats.byCategory} label={(k) => CATEGORY_LABEL[k] ?? k} />
            <StatList title="By root cause" rows={stats.byRootCause} label={(k) => ROOT_CAUSE_LABEL[k] ?? k} />
            <StatList title="By machine" rows={stats.byMachine} label={(k) => k} />
            <StatList title="By outcome" rows={stats.byResolution} label={(k) => k.replace(/_/g, ' ')} />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {TABS.map((t) => (
            <button key={t.key} className={`btn btn-sm ${t.key === tab ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(t.key)}>{t.label}</button>
          ))}
        </div>
        <div className="card-body">
          {rows === null ? (
            <div className="empty-state"><p className="text-muted">Loading…</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state"><p className="text-muted">No complaints here.</p></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>#</th><th>Status</th><th>Problem</th><th>Machine</th><th>Order</th><th>Photos</th><th>Assignee</th><th>Age</th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td><Link to={`/cases/${r.id}`} style={{ fontWeight: 600 }}>#{r.number}</Link></td>
                      <td><StatusBadge status={r.status} /></td>
                      <td>
                        {CATEGORY_LABEL[r.category] ?? r.category}
                        {r.description && <div className="text-muted" style={{ fontSize: '0.8rem', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</div>}
                      </td>
                      <td>{r.machineId ? <Link to={`/machines/${r.machineId}`}>{r.machineName ?? `#${r.machineId}`}</Link> : <span className="text-muted">—</span>}</td>
                      <td>{r.code ? <Link to={`/sessions?code=${r.code}`}>{r.code}</Link> : r.reportedPaidCzk != null ? <span className="text-muted">~{r.reportedPaidCzk} Kč</span> : <span className="text-muted">—</span>}</td>
                      <td>{r.photos > 0 ? `📷 ${r.photos}` : <span className="text-muted">—</span>}</td>
                      <td>{r.assignee ?? <span className="text-muted">—</span>}</td>
                      <td className="text-muted" title={new Date(r.createdAt).toLocaleString()}>{ago(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatList({ title, rows, label }: { title: string; rows: Array<{ key: string; count: number }>; label: (k: string) => string }) {
  return (
    <div>
      <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{title}</div>
      {rows.length === 0 ? <div className="text-muted">—</div> : rows.slice(0, 8).map((r) => (
        <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', padding: '2px 0' }}>
          <span>{label(r.key)}</span><span style={{ fontWeight: 600 }}>{r.count}</span>
        </div>
      ))}
    </div>
  );
}
